/**
 * Public Dashboard Share Route (Dashboard Portal feature)
 *
 * URL: /dashboard-view/:token  (matches the server-minted share URL from
 * buildDashboardShareUrl — `${base}/dashboard-view/<token>`).
 *
 * CLONED from apps/web/src/routes/portal.$token.tsx and adapted for the dashboard
 * share surface. Sibling to __root.tsx — deliberately NOT nested under
 * `_authenticated`. The token IS the credential; no auth state is consulted.
 *
 * Flow (identical to the portal viewer):
 *   1. Fetch meta (GET /api/dashboard-share-view/:token/meta)
 *   2. If inactive → render PortalInactivePage
 *   3. If active + passwordProtected === true → render PortalPasswordGate
 *      - On successful verify, the server sets an HttpOnly cookie; we re-fetch
 *        meta so the flow progresses to the shell.
 *   4. Otherwise render PortalShell (which fetches /view with auto-refresh).
 *
 * REUSE (import, do not clone): PortalInactivePage, PortalPasswordGate, PortalShell
 * from components/portal/** — the dashboard-share-view server routes return
 * byte-identical PortalMeta / PortalViewResponse / PortalVerifyPasswordResponse
 * shapes (the server reports scope "full"), so those viewer components render the
 * dashboard canvas unchanged.
 *
 * NO DashboardLayout wrapper, NO sidebar, NO nav — the minimum surface a client
 * needs to view their shared dashboard.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  fetchDashboardShareMeta,
  dashboardShareShellBackend,
  dashboardSharePasswordGateBackend,
} from "../lib/dashboardShareApi";
import { PortalInactivePage } from "../components/portal/PortalInactivePage";
import { PortalPasswordGate } from "../components/portal/PortalPasswordGate";
import { PortalShell } from "../components/portal/PortalShell";

export const Route = createFileRoute("/dashboard-view/$token")({
  component: DashboardSharePage,
});

const DEFAULT_FAVICON = "/favicon.svg";
const DEFAULT_THEME_COLOR = "#2563eb";

/**
 * Branded favicon + theme-color effect (cloned verbatim from portal.$token.tsx).
 *
 * Favicon precedence: (1) the dedicated uploaded `faviconUrl` when set; (2) else
 * the SVG logo (raster logos look bad at 16x16); (3) else the ProvenLeap default.
 * Captures the prior <link rel="icon"> href + <meta name="theme-color"> content
 * on mount and restores on unmount — prevents DOM leaks during SPA navigation.
 */
function useBrandedFaviconAndThemeColor(
  logoUrl: string | null,
  faviconUrl: string | null,
  primaryColor: string | null,
) {
  useEffect(() => {
    const head = document.head;
    let iconLink = head.querySelector<HTMLLinkElement>("link[rel='icon']");
    let themeMeta = head.querySelector<HTMLMetaElement>("meta[name='theme-color']");

    const createdIcon = iconLink === null;
    const createdTheme = themeMeta === null;
    if (!iconLink) {
      iconLink = document.createElement("link");
      iconLink.setAttribute("rel", "icon");
      head.appendChild(iconLink);
    }
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.setAttribute("name", "theme-color");
      head.appendChild(themeMeta);
    }

    const prevIconHref = createdIcon ? null : iconLink.getAttribute("href");
    const prevThemeContent = createdTheme ? null : themeMeta.getAttribute("content");

    const useSvgFavicon = logoUrl !== null && /\.svg($|\?)/i.test(logoUrl);
    const nextIconHref =
      faviconUrl ?? (useSvgFavicon ? logoUrl : DEFAULT_FAVICON);
    const nextThemeContent = primaryColor ?? DEFAULT_THEME_COLOR;

    iconLink.setAttribute("href", nextIconHref);
    themeMeta.setAttribute("content", nextThemeContent);

    return () => {
      if (createdIcon) {
        iconLink?.remove();
      } else if (prevIconHref !== null) {
        iconLink?.setAttribute("href", prevIconHref);
      }
      if (createdTheme) {
        themeMeta?.remove();
      } else if (prevThemeContent !== null) {
        themeMeta?.setAttribute("content", prevThemeContent);
      }
    };
  }, [logoUrl, faviconUrl, primaryColor]);
}

function DashboardSharePage() {
  const { token } = Route.useParams();
  // Monotonic counter — bumped after password verify so the meta query re-runs
  // (picking up the new portal-session cookie).
  const [metaVersion, setMetaVersion] = useState(0);

  const metaQuery = useQuery({
    queryKey: ["dashboard-share-meta", token, metaVersion],
    queryFn: () => fetchDashboardShareMeta(token),
    retry: false,
    staleTime: Infinity,
  });

  const branding =
    metaQuery.data && metaQuery.data.status === "active"
      ? metaQuery.data.branding
      : null;
  useBrandedFaviconAndThemeColor(
    branding?.logoUrl ?? null,
    branding?.faviconUrl ?? null,
    branding?.primaryColor ?? null,
  );

  if (metaQuery.isLoading) {
    return <DashboardShareLoading />;
  }

  if (metaQuery.isError || !metaQuery.data) {
    // Network error or invalid token shape → treat as inactive for privacy.
    return <PortalInactivePage />;
  }

  const meta = metaQuery.data;

  if (meta.status === "inactive") {
    return <PortalInactivePage reason={meta.reason} />;
  }

  if (meta.passwordProtected) {
    return (
      <PortalPasswordGate
        token={token}
        label={meta.label}
        clientName={meta.clientName}
        logoUrl={meta.branding.logoUrl}
        companyName={meta.branding.companyName}
        poweredByProvenLeap={meta.branding.poweredByProvenLeap}
        onVerified={() => setMetaVersion((v) => v + 1)}
        api={dashboardSharePasswordGateBackend}
      />
    );
  }

  return (
    <PortalShell
      token={token}
      meta={meta}
      onPasswordRequired={() => setMetaVersion((v) => v + 1)}
      api={dashboardShareShellBackend}
    />
  );
}

function DashboardShareLoading() {
  return (
    <main
      id="main-content"
      className="min-h-screen flex items-center justify-center bg-background"
      aria-label="Loading dashboard"
    >
      <div className="h-10 w-10 rounded-full border-[0.5px] border-[var(--hairline)] border-t-foreground animate-spin" />
    </main>
  );
}
